import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Typography,
  Box,
  Paper,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

export default function QrTable({
  mappings = [],
  onRefresh,
  firebaseUser,
  loading,
  selectedStore = 1,
}) {
  const [sortBy, setSortBy] = useState('TABLENUMBER');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleDelete = async (qrCodeId) => {
    try {
      const token = await firebaseUser.getIdToken();

      await axios.delete('/hd-api/herd-serve', {
        params: { id: qrCodeId, storeId: selectedStore },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      onRefresh();
    } catch (error) {
      console.error('Error deleting mapping:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      const token = await firebaseUser.getIdToken();

      await axios.delete(`/hd-api/herd-serve/store=${selectedStore}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      onRefresh();
    } catch (error) {
      console.error('Error clearing all mappings:', error);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedMappings = useMemo(() => {
    const sorted = [...mappings].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (typeof aVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }
    });
    return sorted;
  }, [mappings, sortBy, sortDirection]);

  if (mappings.length === 0) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant='body1' align='center'>
          No QR mappings found.
        </Typography>
      </Paper>
    );
  }
  if (loading) {
    return (
      <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
        <CircularProgress />
        <Typography mt={2}>Loading QR Mappings…</Typography>
      </Paper>
    );
  }
  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={2}
      >
        <Typography variant='h6'>Current QR Mappings</Typography>
        <Button variant='outlined' color='error' onClick={handleClearAll}>
          Clear All
        </Button>
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              sortDirection={sortBy === 'STORESKEY' ? sortDirection : false}
            >
              <TableSortLabel
                active={sortBy === 'STORESKEY'}
                direction={sortDirection}
                onClick={() => handleSort('STORESKEY')}
              >
                Store ID
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sortBy === 'TABLENUMBER' ? sortDirection : false}
            >
              <TableSortLabel
                active={sortBy === 'TABLENUMBER'}
                direction={sortDirection}
                onClick={() => handleSort('TABLENUMBER')}
              >
                Table Number
              </TableSortLabel>
            </TableCell>
            <TableCell
              sortDirection={sortBy === 'QRCODEID' ? sortDirection : false}
            >
              <TableSortLabel
                active={sortBy === 'QRCODEID'}
                direction={sortDirection}
                onClick={() => handleSort('QRCODEID')}
              >
                QR Code ID
              </TableSortLabel>
            </TableCell>
            <TableCell align='right'>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedMappings.map((row) => (
            <TableRow key={row.QRCODEID}>
              <TableCell>{row.STORESKEY}</TableCell>
              <TableCell>{row.TABLENUMBER}</TableCell>
              <TableCell>{row.QRCODEID}</TableCell>
              <TableCell align='right'>
                <IconButton
                  color='default'
                  onClick={() => handleDelete(row.QRCODEID)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
